import { AlertTriangle, Eye } from 'lucide-react'
import RichText from '@/components/RichText'
import type { MessagePreview } from '@/api/messages'

/**
 * The message as the recipient will get it, rendered by the server with the same code that sends it:
 * the real sender (the deal/contact owner), their signature, the filled tokens and the opt-out footer.
 * Styled as an email (or a text bubble) rather than a grey box so it reads as "this is what goes out" —
 * the client's CEO won't send from a CRM whose output he can't see.
 */
export default function MessagePreviewPane({
  preview,
  loading,
  subjectMissing = false,
}: {
  preview: MessagePreview | undefined
  loading: boolean
  /** The sender hasn't typed a subject — show that instead of the server's fallback, which reads as a real subject. */
  subjectMissing?: boolean
}) {
  if (!preview) {
    return loading ? <div className="h-24 rounded-xl bg-gray-50 animate-pulse" aria-label="Loading preview" /> : null
  }
  const isEmail = preview.channel === 'email'
  return (
    <section aria-label="Preview" className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-200 text-[11px] font-medium text-gray-500">
        <Eye size={13} className="text-brand-primary" />
        Preview — exactly what {preview.toName ?? 'the recipient'} will receive
        {loading && <span className="ml-auto text-gray-400 font-normal">Updating…</span>}
      </div>
      {preview.notice && (
        <div className="flex items-start gap-1.5 px-3 py-2 bg-amber-50 text-amber-800 text-xs border-b border-amber-100">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {preview.notice}
        </div>
      )}
      {isEmail ? (
        <div className="p-3">
          <div className="rounded-lg bg-white shadow-sm border border-gray-100">
            <dl className="px-4 pt-3 pb-2.5 border-b border-gray-100 text-xs space-y-0.5">
              <div className="flex gap-2">
                <dt className="w-12 shrink-0 text-gray-400">From</dt>
                <dd className="text-gray-700 min-w-0 break-words">
                  {preview.fromName ?? '—'}
                  {preview.fromAddress && <span className="text-gray-400"> &lt;{preview.fromAddress}&gt;</span>}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-12 shrink-0 text-gray-400">To</dt>
                <dd className="text-gray-700 min-w-0 break-words">
                  {preview.toName ?? '—'}
                  {preview.to && <span className="text-gray-400"> &lt;{preview.to}&gt;</span>}
                </dd>
              </div>
              {subjectMissing ? (
                <div className="pt-1.5 text-sm italic text-amber-700">No subject yet — add one above</div>
              ) : (
                <div className="pt-1.5 text-sm font-semibold text-brand-darkest break-words">{preview.subject}</div>
              )}
            </dl>
            <div className="px-4 py-3">
              <RichText html={preview.body} variant="email" className="text-sm text-gray-700" />
            </div>
          </div>
        </div>
      ) : (
        <div className="p-3">
          <div className="text-[11px] text-gray-400 mb-1.5">
            From {preview.fromName ?? '—'}
            {preview.fromAddress ? ` · ${preview.fromAddress}` : ''} → {preview.to ?? 'no number'}
          </div>
          <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-gray-200 px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap break-words shadow-sm">
            {preview.body}
          </div>
        </div>
      )}
    </section>
  )
}
